// @ts-check

/**
 * Convert base64 encoded data to binary data,
 * to be able to show PDF files larger than 2 MB in iframe tag
 *
 * source: https://stackoverflow.com/a/60256297
 *
 * @param {string} base64
 */
function base64ToBlob(base64) {
  const binStr = Buffer.from(base64, 'base64').toString('binary')
  const len = binStr.length
  const arr = new Uint8Array(len)
  for (let i = 0; i < len; i += 1) {
    arr[i] = binStr.charCodeAt(i)
  }
  return new Blob([arr], { type: 'application/pdf' })
}

/**
 * Returns true if the type of the file is an image.
 * @param {?string} type
 * @param {?boolean} showThumbnail
 * @param {?string} fileThumbnail fileThumbnail is always a base64 jpg
 */
function isImage(type, showThumbnail, fileThumbnail) {
  if (showThumbnail && fileThumbnail) {
    return true
  }

  return !!type?.match('image/*')
}

/**
 * Returns true if the type of the file is Excel spreadsheet.
 * @param {?string} type
 */
function isXlsx(type) {
  return (
    !!type?.match('officedocument.spreadsheetml.sheet') ||
    !!type?.match('application/vnd.ms-excel')
  )
}

/**
 * Returns true if the type of the file is PDF.
 * @param {?string} type
 * @param {?boolean} showThumbnail
 * @param {?string} fileThumbnail fileThumbnail is always a base64 jpg
 */
function isPdf(type, showThumbnail, fileThumbnail) {
  if (showThumbnail && fileThumbnail) {
    return false
  }

  return !!type?.match('application/pdf')
}

/**
 * @param {?string} file
 * @param {?boolean} showThumbnail
 * @param {?string} fileThumbnail fileThumbnail is always a base64 jpg
 * @param {?string} type
 */
function src(file, showThumbnail, fileThumbnail, type) {
  if (!file || !type) {
    return null
  }

  // If thumbnail is given return it instead of PDF blob for mobile application
  if (showThumbnail && fileThumbnail) {
    return `data:image/jpeg;base64,${fileThumbnail}`
  }

  if (isPdf(type)) {
    const blob = base64ToBlob(file)
    return URL.createObjectURL(blob)
  }

  return `data:${type};base64,${file}`
}

export const present = ({ file, showThumbnail, fileThumbnail, type }) => ({
  isImage: isImage(type, showThumbnail, fileThumbnail),
  isPdf: isPdf(type, showThumbnail, fileThumbnail),
  isXlsx: isXlsx(type),
  src: src(file, showThumbnail, fileThumbnail, type)
})

export default {
  present
}
